import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Linkedin, Send, ExternalLink, Tag } from "lucide-react";
import FadeIn from "@/components/FadeIn";
import SeoHead from "@/components/SeoHead";
import { SERVICES } from "@/data/services";
import { SECTORS } from "@/data/sectors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GENERAL_TOPIC = "Algemene vraag of kennismaking";
const DIENST_TOPICS = SERVICES.map((s) => s.navLabel);
const SECTOR_TOPICS = SECTORS.map((s) => s.navLabel);
const ALL_TOPICS = [GENERAL_TOPIC, ...DIENST_TOPICS, ...SECTOR_TOPICS];

const initialForm = {
  naam: "",
  organisatie: "",
  functie: "",
  email: "",
  telefoon: "",
  onderwerp: GENERAL_TOPIC,
  bericht: "",
};

export default function Contact() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(initialForm);
  const [contextTopic, setContextTopic] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const q = searchParams.get("onderwerp");
    if (q && ALL_TOPICS.includes(q) && q !== GENERAL_TOPIC) {
      setContextTopic(q);
      setForm((f) => {
        const next = { ...f, onderwerp: q };
        if (SECTOR_TOPICS.includes(q) && !f.bericht) {
          next.bericht = `Wij zijn actief in de sector ${q}. Graag verkennen wij welke vragen rond AI governance, datarisico's en digitale regie binnen onze organisatie bestuurlijke aandacht vragen.`;
        }
        return next;
      });
    }
  }, [searchParams]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const buildMailto = () => {
    const subject = encodeURIComponent("Kennismaking SovEU");
    const lines = [
      `Naam: ${form.naam}`,
      `Organisatie: ${form.organisatie}`,
      `Functie: ${form.functie}`,
      `E-mail: ${form.email}`,
      `Telefoon: ${form.telefoon || "-"}`,
      `Onderwerp: ${form.onderwerp}`,
      "",
      "Bericht:",
      form.bericht,
    ];
    const body = encodeURIComponent(lines.join("\n"));
    return `mailto:info@soveu.nl?subject=${subject}&body=${body}`;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.naam || !form.organisatie || !form.functie || !form.email || !form.bericht) {
      toast.error("Vul alstublieft alle verplichte velden in.");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Dank voor uw bericht. U ontvangt binnen één werkdag een reactie.");
      setForm(initialForm);
    } catch (err) {
      // Verzamel zo veel mogelijk diagnostische info
      const status = err?.response?.status;
      const statusText = err?.response?.statusText;
      const rawDetail = err?.response?.data?.detail;
      const targetUrl = `${API}/contact`;
      const networkError = !err?.response;

      // Pydantic 422 returns: [{loc:[...], msg, type, input}, ...]
      const formatDetail = (d) => {
        if (!d) return null;
        if (typeof d === "string") return d;
        if (Array.isArray(d)) {
          return d
            .map((e) => {
              const field = Array.isArray(e?.loc)
                ? e.loc.filter((s) => s !== "body").join(".")
                : "";
              const msg = e?.msg || e?.message || "ongeldig";
              return field ? `${field}: ${msg}` : msg;
            })
            .join("; ");
        }
        if (typeof d === "object") {
          return d.msg || d.message || JSON.stringify(d);
        }
        return String(d);
      };
      const detail = formatDetail(rawDetail);

      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error("Contact form submit failed:", {
          message: err?.message,
          status,
          statusText,
          rawDetail,
          url: targetUrl,
          networkError,
        });
      }

      // Toon zichtbare diagnose in toast (helpt user + agent debuggen)
      let diagText;
      if (networkError) {
        diagText = `Netwerk-/CORS-fout — backend onbereikbaar vanaf ${window.location.host}.`;
      } else if (status === 404) {
        diagText = `Backend niet gevonden (404) op ${targetUrl}.`;
      } else if (status >= 500) {
        diagText = `Server-fout (${status}). ${detail || "Backend logs raadplegen."}`;
      } else if (status === 422) {
        diagText = `Velden niet geldig: ${detail || "controleer de invoer"}.`;
      } else {
        diagText = `Fout ${status || ""}: ${detail || err?.message || "onbekend"}`;
      }

      toast.error(
        `Verzenden mislukt. ${diagText} Uw mailprogramma wordt geopend zodat u het bericht alsnog kunt versturen.`,
        { duration: 8000 }
      );
      setTimeout(() => {
        window.location.href = buildMailto();
      }, 800);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact" className="pt-[96px]">
      <SeoHead
        title="Contact — SovEU"
        description="Neem contact op met SovEU voor een vrijblijvend gesprek over AI governance, datagovernance, algoritmen, cloudafhankelijkheid en bestuurlijke digitale regie."
        path="/contact"
      />
      <section className="section bg-white">
        <div className="container-soveu">
          <FadeIn className="max-w-3xl">
            <div className="eyebrow mb-4">Contact</div>
            <h1 className="h1-display mb-6">Neem contact op</h1>
            <p className="body-lg text-[#334155]">
              Vul het formulier in of mail direct naar{" "}
              <a
                href="mailto:info@soveu.nl?subject=Kennismaking%20SovEU"
                className="underline underline-offset-4 font-medium"
                style={{ color: "#1E3A5F" }}
                data-testid="contact-intro-mail"
              >
                info@soveu.nl
              </a>
              . U ontvangt binnen één werkdag een reactie.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-soveu grid lg:grid-cols-12 gap-10">
          {/* Form */}
          <FadeIn className="lg:col-span-7">
            {contextTopic && (
              <div
                className="inline-flex items-center gap-2 mb-5 px-3.5 py-1.5 rounded-full text-[13px]"
                style={{ backgroundColor: "#F5F7FA", border: "1px solid #E5E9EF", color: "#1E3A5F" }}
                data-testid="contact-context-badge"
              >
                <Tag size={13} strokeWidth={2} style={{ color: "#2D6F8F" }} />
                <span>
                  U vroeg naar: <span className="font-semibold">{contextTopic}</span>
                </span>
              </div>
            )}
            <form
              onSubmit={onSubmit}
              className="soveu-card p-8 md:p-10 space-y-5"
              data-testid="contact-form"
              noValidate
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Naam" name="naam" value={form.naam} onChange={onChange} required testId="contact-field-naam" />
                <Field label="Organisatie" name="organisatie" value={form.organisatie} onChange={onChange} required testId="contact-field-organisatie" />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Functie" name="functie" value={form.functie} onChange={onChange} required testId="contact-field-functie" />
                <Field label="E-mailadres" name="email" type="email" value={form.email} onChange={onChange} required testId="contact-field-email" />
              </div>
              <Field label="Telefoonnummer (optioneel)" name="telefoon" value={form.telefoon} onChange={onChange} testId="contact-field-telefoon" />
              <div>
                <label htmlFor="onderwerp" className="block text-[14px] font-medium text-[#1E3A5F] mb-2">
                  Welk onderwerp bekeek u? <span className="text-[#A0241C]">*</span>
                </label>
                <select
                  id="onderwerp"
                  name="onderwerp"
                  value={form.onderwerp}
                  onChange={onChange}
                  className="soveu-input"
                  data-testid="contact-field-onderwerp"
                >
                  <option value={GENERAL_TOPIC}>{GENERAL_TOPIC}</option>
                  <optgroup label="Diensten">
                    {DIENST_TOPICS.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Sectoren">
                    {SECTOR_TOPICS.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </optgroup>
                </select>
                <p className="text-[12px] text-[#6B7280] mt-1.5">
                  Zo kunnen wij ons gesprek beter laten aansluiten op uw vraag.
                </p>
              </div>
              <div>
                <label htmlFor="bericht" className="block text-[14px] font-medium text-[#1E3A5F] mb-2">
                  Vraag of opmerking <span className="text-[#A0241C]">*</span>
                </label>
                <textarea
                  id="bericht"
                  name="bericht"
                  rows={6}
                  required
                  value={form.bericht}
                  onChange={onChange}
                  className="soveu-input resize-y"
                  data-testid="contact-field-bericht"
                  placeholder="Vertel kort waar het om gaat — bijvoorbeeld de aanleiding, de organisatie en de fase waarin u zich bevindt."
                />
              </div>

              <p className="text-[13px] text-[#6B7280]">
                Door te verzenden stemt u ermee in dat wij uw gegevens gebruiken om contact met u op te nemen. Zie ons{" "}
                <a href="/privacybeleid" className="underline" style={{ color: "#1E3A5F" }}>
                  privacybeleid
                </a>
                .
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="contact-submit"
                >
                  {submitting ? "Bezig met verzenden..." : "Verstuur bericht"}
                  {!submitting && <Send size={16} />}
                </button>
                <a
                  href={buildMailto()}
                  data-testid="contact-mailto-fallback"
                  className="text-[14px] text-[#2D6F8F] hover:text-[#1E3A5F] underline-offset-4 hover:underline transition-colors"
                >
                  Liever via mail? Open in mailprogramma
                </a>
              </div>

              <p className="text-[14px] text-[#334155] mt-2 pt-4 border-t border-[#E5E9EF]" data-testid="contact-fallback-text">
                U kunt ook rechtstreeks mailen naar{" "}
                <a
                  href="mailto:info@soveu.nl?subject=Kennismaking%20SovEU"
                  className="font-medium underline underline-offset-4"
                  style={{ color: "#1E3A5F" }}
                  data-testid="contact-fallback-mail"
                >
                  info@soveu.nl
                </a>
                .
              </p>
            </form>
          </FadeIn>

          {/* Aside */}
          <FadeIn delay={120} className="lg:col-span-5">
            <aside className="soveu-card p-8 h-full flex flex-col" data-testid="contact-aside">
              <h2 className="h3-display mb-6">Direct contact</h2>

              <ul className="space-y-5">
                <li className="flex items-start gap-4">
                  <span
                    className="w-10 h-10 rounded-md inline-flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: "#F5F7FA", color: "#1E3A5F" }}
                  >
                    <Mail size={18} strokeWidth={1.75} />
                  </span>
                  <div>
                    <div className="text-[13px] font-semibold tracking-[0.12em] uppercase mb-1" style={{ color: "#1E3A5F" }}>
                      E-mail
                    </div>
                    <a
                      href="mailto:info@soveu.nl?subject=Kennismaking%20SovEU"
                      className="text-[#1A1A1A] hover:text-[#1E3A5F] transition-colors text-[15px]"
                      data-testid="contact-aside-email"
                    >
                      info@soveu.nl
                    </a>
                  </div>
                </li>

                <li className="flex items-start gap-4">
                  <span
                    className="w-10 h-10 rounded-md inline-flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: "#F5F7FA", color: "#1E3A5F" }}
                  >
                    <Linkedin size={18} strokeWidth={1.75} />
                  </span>
                  <div>
                    <div className="text-[13px] font-semibold tracking-[0.12em] uppercase mb-1" style={{ color: "#1E3A5F" }}>
                      LinkedIn
                    </div>
                    <a
                      href="https://www.linkedin.com/company/soveu/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-[#1A1A1A] hover:text-[#1E3A5F] transition-colors text-[15px]"
                      data-testid="contact-aside-linkedin"
                    >
                      linkedin.com/company/soveu
                      <ExternalLink size={13} />
                    </a>
                  </div>
                </li>
              </ul>

              <div className="mt-auto pt-8 border-t border-[#E5E9EF] text-[14px] text-[#334155] leading-relaxed">
                <p className="mb-3">
                  <strong style={{ color: "#1E3A5F" }}>Liever eerst bellen?</strong> Stuur een mail met uw telefoonnummer en een voorkeur voor tijd — wij bellen u binnen één werkdag terug.
                </p>
                <p className="text-[12px] text-[#6B7280]">KVK 42033105</p>
              </div>
            </aside>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required = false, testId }) {
  return (
    <div>
      <label htmlFor={name} className="block text-[14px] font-medium text-[#1E3A5F] mb-2">
        {label} {required && <span className="text-[#A0241C]">*</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="soveu-input"
        data-testid={testId}
      />
    </div>
  );
}
